import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useMemo, useState } from 'react';
import { match, P } from 'ts-pattern';

import { MessageMedium, WhatsAppMedium } from 'constants/messageMediums';
import { useLocaleSettingsContext } from 'modules/campaigns/lib/contexts/LocaleSettingsContext';
import { Locale } from 'modules/domains/template';
import { useFilterLocales } from 'modules/templateEditor/hooks/useFilterLocales';
import { Trans } from 'services/i18n';
import { FormField } from 'ui/formField';
import { Select } from 'ui/selectv2/Select';
import { OnSelectHandler } from 'ui/selectv2/types';
import { colors } from 'ui/theme';
import { Nullish } from 'utils/tsHelpers';

import { useTranslations } from '../../hooks/useTranslations';
import { DEFAULT_LOCALE_SELECT_TEST_IDS } from './DefaultLocaleSelectFormField.constants';
import { SelectWrapper } from './DefaultLocaleSelectFormField.styled';
import {
  getDefaultLocaleSelectCategoryLabels,
  getLocaleOptions,
  toLocaleOption,
} from './DefaultLocaleSelectFormField.utils';
import { DefaultLocaleSelectFormFieldBanner } from './DefaultLocaleSelectFormFieldBanner';
import { useFirstMountState } from 'react-use';

type DefaultLocaleSelectFormFieldProps = {
  onSelect: (defaultLocale: Locale) => void;
  messageMedium?: MessageMedium | WhatsAppMedium;
  activeTemplateLocales?: Locale[];
  templateDefaultLocale?: Nullish<Locale>;
  /** Whether the default locale is set at the project level */
  isProjectLevelDefaultLocale?: boolean;
};

/**
 * WARNING: the below spec may no longer be accurate.
 * We have added a lot of new features to this component and may need to update the spec.
 *
 * Expected behavior/usage:
 * - TemplateAndProjectLocales (in parent component) endpoint will return activeTemplateLocales and
 *   templateDefaultLocale
 * - DefaultLocaleSelectFormField should default to Project Locales when activeTemplateLocales are not
 *   provided (example: new template)
 * - For existing templates:
 *   - activeLocales should be options
 *   - default locale should be templateDefaultLocale
 * - For new templates:
 *   - projectLocales should be options
 *   - default locale should be project locale default (first index of project locales array)
 */
export const DefaultLocaleSelectFormField = ({
  onSelect,
  messageMedium,
  activeTemplateLocales = [],
  templateDefaultLocale = {} as Nullish<Locale>,
  isProjectLevelDefaultLocale,
}: DefaultLocaleSelectFormFieldProps) => {
  const { t, ready } = useTranslations();
  const {
    locales: projectLocales,
    validWhatsAppLocales,
    invalidWhatsAppLocales,
    isTemplateDefaultLocaleModifiable,
  } = useLocaleSettingsContext();
  const projectDefaultLocale = projectLocales?.[0];
  const templateDefaultLocaleTag = templateDefaultLocale?.tag;
  const projectDefaultLocaleTag = projectDefaultLocale?.tag || '';

  const activeLocales = isTemplateDefaultLocaleModifiable
    ? activeTemplateLocales
    : activeTemplateLocales.filter((tl) =>
        [templateDefaultLocaleTag, projectDefaultLocaleTag].includes(tl.tag),
      );

  const isExistingTemplate = !isEmpty(activeLocales);
  const initialDefaultLocale = isExistingTemplate ? templateDefaultLocale : projectDefaultLocale;

  const translateDefaultLocaleSuffix = () =>
    isProjectLevelDefaultLocale ? t('label.projectDefault') : t('label.default');
  const fallbackDefaultLocaleSuffix = isProjectLevelDefaultLocale ? 'Project default' : 'Default';
  const defaultLocaleSuffix = ready ? translateDefaultLocaleSuffix() : fallbackDefaultLocaleSuffix;

  const [selectedDefaultLocale, setSelectedDefaultLocale] = useState<Nullish<Locale>>();

  const formattedLocaleOptions = useMemo(
    () =>
      getLocaleOptions({
        isExistingTemplate,
        activeLocales,
        projectLocales,
        initialDefaultLocale,
        defaultLocaleSuffix,
        validWhatsAppLocales,
        invalidWhatsAppLocales,
        messageMedium,
      }),
    [
      isExistingTemplate,
      activeLocales,
      projectLocales,
      initialDefaultLocale,
      defaultLocaleSuffix,
      validWhatsAppLocales,
      invalidWhatsAppLocales,
      messageMedium,
    ],
  );

  const { options, handleFilter } = useFilterLocales(formattedLocaleOptions, defaultLocaleSuffix);

  const handleSelect: OnSelectHandler = ([option]) => {
    const selected = projectLocales.find(({ tag } = {} as any) => tag === option.value);
    if (selected) {
      onSelect(selected);
      setSelectedDefaultLocale(selected);
    }
  };

  const isWhatsAppMedium = messageMedium === WhatsAppMedium.WhatsApp;
  const hasValidWhatsAppLocales = (validWhatsAppLocales?.length ?? 0) > 0;
  const hasInvalidWhatsAppLocales = (invalidWhatsAppLocales?.length ?? 0) > 0;
  const isWhatsAppMediumWithNoValidLocales = isWhatsAppMedium && !hasValidWhatsAppLocales;

  const isTemplateDefaultDifferentThanProject = !!(
    !isWhatsAppMediumWithNoValidLocales &&
    templateDefaultLocaleTag &&
    projectDefaultLocaleTag &&
    templateDefaultLocaleTag !== projectDefaultLocaleTag
  );

  const isSelectedDefaultLocaleDifferentThanProject = !!(
    selectedDefaultLocale?.tag &&
    projectDefaultLocaleTag &&
    selectedDefaultLocale?.tag !== projectDefaultLocaleTag
  );

  const defaultValue = useMemo(() => {
    const isInitialDefaultLocaleSupportedByWhatsApp = !!validWhatsAppLocales?.find(
      ({ tag }) => !!tag && tag === initialDefaultLocale?.tag,
    );

    return match({
      locale: initialDefaultLocale,
      suffix: defaultLocaleSuffix,
      isWhatsAppMedium,
      isWhatsAppMediumWithNoValidLocales,
      isInitialDefaultLocaleSupportedByWhatsApp,
    })
      .with({ isWhatsAppMediumWithNoValidLocales: true }, () => [])
      .with({ isInitialDefaultLocaleSupportedByWhatsApp: false, isWhatsAppMedium: true }, () => [])
      .with({ locale: { tag: P.string } }, ({ locale, suffix }) => [
        toLocaleOption({ locale, suffix }),
      ])
      .otherwise(() => []);
  }, [
    initialDefaultLocale,
    defaultLocaleSuffix,
    validWhatsAppLocales,
    isWhatsAppMediumWithNoValidLocales,
    isWhatsAppMedium,
  ]);

  const isFirstMount = useFirstMountState();

  const value = selectedDefaultLocale?.tag
    ? [toLocaleOption({ locale: selectedDefaultLocale, suffix: defaultLocaleSuffix })]
    : undefined;

  useEffect(() => {
    /**
     * Sometimes the default value is not set on the first render, so we need to set it manually.
     * For example, this may happen when the data is fetched from a remote server after the component is mounted.
     */
    if (!isFirstMount && !value && defaultValue?.length) {
      setSelectedDefaultLocale(defaultValue[0]?.data);
    }
  }, [defaultValue, isFirstMount, value]);

  const categoryLabels = getDefaultLocaleSelectCategoryLabels({
    isWhatsAppMedium,
    hasValidWhatsAppLocales,
    hasInvalidWhatsAppLocales,
  });

  return (
    <FormField
      data-test={`${DEFAULT_LOCALE_SELECT_TEST_IDS.dropdown}-form-field`}
      labelText={
        <Trans
          t={t}
          i18nKey="input.defaultLocale.label"
          components={{
            span: <span style={{ color: colors.red100 }} />,
          }}
        >
          Default locale<span style={{ color: colors.red100 }}>*</span>
        </Trans>
      }
    >
      <SelectWrapper>
        <Select
          usePortal
          showCategoryDivider
          value={value}
          defaultValue={defaultValue}
          options={options}
          isVirtualized={options.length > 100}
          id={DEFAULT_LOCALE_SELECT_TEST_IDS.dropdown}
          onSelect={handleSelect}
          placeholder={t('input.defaultLocale.placeholder')}
          onInputChange={debounce(handleFilter, 300)}
          categoryLabels={categoryLabels}
          isOptionDisabled={(option) => !!option.isDisabled}
        />
      </SelectWrapper>

      <DefaultLocaleSelectFormFieldBanner
        templateDefaultLocale={templateDefaultLocale}
        selectedDefaultLocale={selectedDefaultLocale}
        isTemplateDefaultLocaleModifiable={isTemplateDefaultLocaleModifiable}
        isTemplateDefaultDifferentThanProject={isTemplateDefaultDifferentThanProject}
        isSelectedDefaultLocaleDifferentThanProject={isSelectedDefaultLocaleDifferentThanProject}
      />
    </FormField>
  );
};
